<script module>
		import { user, view } from "../client.svelte.js";
		import AdvtsCatalog from "./AdvtsCatalog.svelte";
		import Adventure from "./Adventure.svelte";
		import Structure from "./Structure.svelte";
		import Section from "./Section.svelte";
		import SitesCatalog from "./SitesCatalog.svelte";
		import Site from "./Site.svelte";
		import CharsCatalog from "./CharsCatalog.svelte";
		import Character from "./Character.svelte";
		import Deploy from "./Deploy.svelte";
		import Rewards from "./Rewards.svelte";

		const infoAdvt = "Allgemeine Informationen zum Abenteuer wie Name, Gegend oder Jahreszeit. Aber auch das Titelbild und eine eine kurze Einleitung."
		const infoSites = "Komplexere Schauplätze umfassen mehrere Karten und Hindernisse innerhalb der Karte."
		const infoChars = "Erstellen der Nicht-Spieler-Charaktere für das gesamte Abenteuer. Dieses können Varianten von Charakteren aus dem Katalog sein oder eigens erstellte Charaktere."

		/** pages of the prepare module: 'label' pages are shown as tabs, 'tab' is the highlighted tab, 'clears' are the selections
		 *  reset when navigating to the page via tab or breadcrumb, 'requires' is the selection needed to show the page **/
		export const advtLayouts = [
			{page: 'catalog', Page: AdvtsCatalog, title: "Abenteuerübersicht", info: infoAdvt },
			{page: 'general', Page: Adventure, title: "Abenteuer allgem. Informationen", label: "Abenteuer", icon: 'scroll', tab: 'general',
					clears: ['section', 'site', 'character'], info: infoAdvt },
			{page: 'structure', Page: Structure, title: "Abenteuer strukturieren", label: "Abschnitte", icon: 'list-ol', tab: 'structure',
					clears: ['site', 'character'],
					info: "Erstelle die einzelnen Abschnitte (Erzählungen, Szenen, Reisen) des Abenteuers und lege die normale Abfolge der Abschnitte innerhalb des Abenteuers fest."	},
			{page: 'section', Page: Section, title: "Abschnitt ausgestalten", tab: 'structure', requires: 'section', clears: ['site', 'character'],
					info: "Lade die Bilder für einen Abschnitt hoch und beschreibe den Plot. Wähle die Karte für Begegnungen innerhalb von Szenen aus."	},
			{page: 'deploy', Page: Deploy, title: "Begegnung vorbereiten", tab: 'structure', requires: 'section',
					info: "Zur Vorbereitung einer Begegnung in einer Szene können NSC an den passenden Stellen der Karte aufgestellt werden."	},
			{page: 'sites', Page: SitesCatalog, title: "Orte anzeigen", label: "Karten und Orte", icon: 'map', tab: 'sites',
					clears: ['section', 'character'], info: infoSites },
			{page: 'siteDetails', Page: Site, title: "Ort bearbeiten", tab: 'sites', requires: 'site', info: infoSites },
			{page: 'characters', Page: CharsCatalog, title: "Charaktere auswählen", label: "Antagonisten", icon: 'person', tab: 'characters',
					clears: ['section', 'site'], info: infoChars },
			{page: 'charDetails', Page: Character, title: "NSC-Charakter bearbeiten", tab: 'characters', requires: 'character', info: infoChars },
			{page: 'rewards',  Page: Rewards, title: "Belohnung erstellen", label: "Belohnung", icon: 'coins', tab: 'rewards',
					clears: ['section', 'site', 'character'],
					info: "Der Lohn für das Bestehen des Abenteuers: Erfahrungspunkte, Geld und besondere Gegenstände." }
		].map(layout => Object.assign(layout, {module: 'advt'}))

		const requiredLabels = {section: "Abschnitt", site: "Ort", character: "Charakter"}

		/** label and tag style of the section types **/
		export const sectTyp = {Story: "Story", Scene: "Szene", Journey: "Reise"}
		export const sectTag = {Story: 'neutral', Scene: 'accent', Journey: 'outline'}
		export const sect_label = (sect) => `${sectTyp[sect.ZUB] ?? sect.ZUB}: ${sect.name}`

		/** site of a section (scenes only) **/
		export const site_of = (sect) => (sect?.ZUB === 'Scene') ? user.sites.get(sect.siteID ?? sect.ID) ?? null : null

		/** non-player characters deployed in a section **/
		export function particips_of(advt, sectKey) {
				const charIDs = new Set(
					user.deployments.values()
						.filter(deploy => deploy.ADVT === advt.ID && deploy.SECT === sectKey && deploy.charID !== 'HEROES')
						.map(deploy => deploy.charID))
				return charIDs.values()
						.map(charID => user.characters.get(charID))
						.filter(char => char && char.isIndividuum)
						.toArray()
		}

		/** characters prepared for the adventure **/
		export const advt_characters = (advt) => (advt) ?
			user.characters.values().filter(char => char.ZUB !== 'Hero' && char.rubric === advt.ID).toArray() : []

		/** switches to a page of the module and resets the selections cleared by this page **/
		export function goto_page(page) {
				const layout = advtLayouts.find(layout => layout.page === page)
				for (const elem of layout?.clears ?? [])
					view[elem] = null
				view.switch_layout(`advt-${page}`)
		}
</script>


<script>
		import Help from "../elements/Help.svelte";
		import Breadcrumbs from "../elements/Breadcrumbs.svelte";
		import ModuleTabs from "../elements/ModuleTabs.svelte";

		let page = $derived(view.layout.page)
		let showTabs = $derived(page !== 'catalog' && !!view.adventure)

		let counts = $derived((view.adventure) ? {
				structure: view.adventure.sections.map(sectID => user.sections.get(sectID)).length,
				sites: user.sites.values().filter(site => site.rubric === view.adventure.ID).toArray().length,
				characters: advt_characters(view.adventure).length,
				rewards: view.adventure.rewards?.length ?? 0 } : {})
		let tabs = $derived(advtLayouts.filter(layout => layout.label)
			.map(layout => ({id: layout.page, label: layout.label, icon: layout.icon, info: layout.info, count: counts[layout.page]})))

		/** breadcrumbs: path from the adventure catalog to the current page (sections, sites and characters as subpages);
		 *  the overview pages of sections, sites and characters are reached via the tabs **/
		let trail = $derived.by(() => {
				const root = {label: "Abenteuer vorbereiten", page: 'catalog'}
				if (page === 'catalog' || !view.adventure)
					return {crumbs: [], current: root.label}
				const advtLabel = `${view.adventure.ID} · ${view.adventure.name ?? ""}`
				const toAdvt = [root, {label: advtLabel, page: 'general'}]
				const toSect = (view.section) ? [...toAdvt, {label: sect_label(view.section), page: 'section'}] : null
				switch (page) {
					case 'section':
						return {crumbs: toAdvt, current: (view.section) ? sect_label(view.section) : "Abschnitt"}
					case 'deploy':
						return {crumbs: toSect ?? toAdvt, current: "Aufstellung"}
					case 'siteDetails':
						return {crumbs: toSect ?? toAdvt, current: view.site?.name ?? "Ort"}
					case 'charDetails':
						return {crumbs: toSect ?? toAdvt, current: view.character?.name ?? "Charakter"}
					default:
						return {crumbs: [root], current: advtLabel}
				}
		})
</script>


<!-------------------------------- HTML -------------------------------------->

<main id='prepare' class:noTabs={!showTabs}>

	<!-- module navigation -->
	<Breadcrumbs back={page !== 'catalog'} current={trail.current}
	           crumbs={trail.crumbs.map(crumb => ({label: crumb.label, onclick: () => goto_page(crumb.page)}))} />

	<!-- module tabs -->
	{#if showTabs}
		<ModuleTabs tabs={tabs} selected={view.layout.tab} onselect={goto_page} />
	{/if}

	<view.layout.Page />

	<Help/>

</main>


<!-------------------------------- STYLES -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	#prepare
		display: grid
		grid-template-columns: var(--menu-width) minmax(0, 1fr) var(--help-width, 0)
		grid-template-rows: 51px auto minmax(0, 1fr)
		grid-template-areas: "panel mnav help" "panel head help" "panel main help"
		--sidebar-top: 102px		// height of navigation bar and tabs (top of the sidebar flyouts)

		&.noTabs
			--sidebar-top: 51px

</style>
